import _ from 'underscore';

import Collection from '../collection';
import Fields from '../collection/refbooks/fields';
import Model from './model';

import { hashify } from '../utils/arrays';
import { flatify } from './refbooks/field.utils';





const Task = Model.extend( {

    defaults() {
        return {
            id: undefined,
            contract_id: undefined, contract_title: undefined, contract: undefined,
            service_object_layer_id: undefined,
            service_object_id: undefined, service_object_title: undefined, 
            organization_id: undefined,
            assigned_organization_id: undefined,
            assigned_user_id: undefined,
            assigned_user_fio: undefined,
            type_id: undefined,
            status_id: undefined,
            priority_id: undefined,
            title: undefined,
            text: undefined,
            fields: {},
            stage: undefined,
            deadline: undefined,
            attachments: []
        };
    },

    endpoint: 'tasks?apiVersion=2.1', responseAt: 'task',

    assign({ id, fio, organization_id }) {
        const props = { 
            assigned_user_id: id, 
            assigned_user_fio: fio 
        };
        if (+organization_id)
            props.assigned_organization_id = organization_id;

        this.set( props );
    },

    renameProperties: {
        zoom: 'point_zoom'
    },

    customParser(payload, data = {}) {
        const { 
            point, lon, lat,
            assigned_organization_id, assigned_user_id,
            attachments,
            ...others,
        } = payload;

        const { 
            capabilities, 
            comments,  lastComment,
            schedule,
        } = data;

        if (capabilities)
            this.capabilities_ = capabilities;
        if (comments)
            this.comments_ = comments;

        const result = {
            point: lon & lat ? [+lon, +lat] : point,
            assigned_organization_id: +assigned_organization_id || 0,
            assigned_user_id: +assigned_user_id || 0,
            attachments: (attachments || [])
                // swap file and file_name (different meaning in models.Files and forms.Attachment);
                // here we use forms.Attachment-style
                .map( ({name, file_name, ...file}) => ({ file: file_name, file_name: name, ...file }) )
                .sort( (f0, f1) => f0.id - f1.id ),

            // the last comment is the first in the list
            lastComment: comments ? comments[0] : lastComment,
            schedule,

            ...others
        };

        return result;
    },

    diff(anotherModel, options = {}) {
        const coll = options.fields instanceof Collection ? options.fields.toJSON() : options.fields;
        const fields = hashify( 
            flatify( new Fields( coll || [] ).nonSystem() ), 
            field => [field.translit, field] );

        const addedAndImmediatelyDeletedAttachments = hashify( this.get( 'attachments' )
                .filter( att => att.guid )
                .filter( att => att.deleted === true || att.deleted === 't' )
                .map( att => att.guid ) );

        const diff = Model.prototype.diff.call( this, anotherModel, options )
            .filter( ({ attrName, newValue, oldValue }) => {
                // ignore - field is set automatically by suggestion
                if (attrName === 'assigned_user_fio')
                    return false;

                if (attrName === 'fields')
                    return diffFields( newValue, oldValue );

                if (attrName === 'attachments')
                    return diffAttachments( newValue, oldValue );

                return true;
            } );

        const hash = hashify( diff.map( d => d.attrName ) );
        if (!hash['attachments'])
            if (diffAttachments( this.get( 'attachments' ) ))
                diff.push( {
                    attrName: 'attachments',
                    newValue: this.get( 'attachments' ),
                    oldValue: anotherModel.get( 'attachments' ),
                } )

        return diff;

        function diffFields(newValue, oldValue) {

            const valDiffer = (field, nv, ov) => {
                if (field.format === 'file')
                    return hash( ov ) !== hash( (nv || [])
                        // exclude add-and-immediately-delete attachments
                        .filter( att => !addedAndImmediatelyDeletedAttachments[att.guid] ) );

                if (field.format !== 'composite')
                    return hash( nv ) !== hash( ov );

                // no user interaction with composites -> nv is the same as ov
                // despite new inputs in interface according to auto-expanding;
                if (nv.some( (instance, index ) => difference( instance, ov[index] || {} ) ))
                    return true;

                // missing some old-element
                const afterEls = hashify( nv.filter( el => el.guid ), ({ guid }) => [guid, true] );
                if (!ov.every( el => afterEls[el.guid] ))
                    return true;

                // structure of els:
                // ...old-els ...auto-added else | ...user-added els
                //                               ^ min-count
                //
                // Suppose there are no changes in auto-added and user-added inputs.
                // We must check if there are user-added els or not.
                // If lengths are equal:
                //    1) only auto-added - no changes
                //    2) user-added + auto-added removed - no changes
                // 
                // So, check whether length is greater than min-count or not.
                // !AND! those elements have no guids
                if (field.min_count && nv.length > +field.min_count)
                    return nv
                        .filter( (el, index) => index >= +field.min_count)
                        .some( el => !el.guid );
            }

            // todo potential troubles with deletions in nested composites
            const difference = (newInstance, { guid, ...oldInstance }) => {
                const copyOldInstance = { ...oldInstance };

                for (let translit in newInstance) if (translit != 'guid') {
                    const nv = newInstance[translit].value;
                    if (translit in copyOldInstance)
                        // field is deleted -> ignore
                        if (!fields[translit])
                            delete copyOldInstance[translit];
                        else if (valDiffer( fields[translit], nv, copyOldInstance[translit].value ))
                            return true;
                        else
                            delete copyOldInstance[translit];
                    // else if (console.log( translit ) || fields[translit].format === 'composite')
                    //     if (valDiffer( translit, nv, [] ))
                    //         return true;
                    //     else
                    //         ;
                    else if (fields[translit] && fields[translit].format === 'file' && nv instanceof Array) {
                        const nonImmediatelyDeleted = nv
                            .filter( att => !addedAndImmediatelyDeletedAttachments[att.guid] );
                        if (nonImmediatelyDeleted.length > 0)
                            return true;
                    } else if (nv !== undefined && nv !== null && 
                             nv !== '' &&
                             nv !== false)
                        return true;
                }

                return Object.keys( copyOldInstance ).length > 0;
            };

            const hash = json => JSON.stringify( 
                typeof json !== 'object' || json == null
                    ? json
                    : Object.keys( json )
                        .sort()
                        .map( key => [key, json[key]] )
                        .map( ([key, value]) => typeof value === 'object'
                            ? [key, hash( value )]
                            : [key, value] )
                        .reduce( (hash, [key, value]) => (hash[key] = value, hash), {} ) );

            return difference( newValue, oldValue );
        }

        function diffAttachments(newValue/*, oldValue*/) {
            const deleted = newValue
                .filter( att => att.id != undefined )
                .filter( ({ deleted }) => deleted == true || deleted == 't' );

            const added = newValue
                .filter( att => att.id == undefined )
                .filter( ({ deleted }) => deleted == undefined || deleted == false || deleted == 'f' );

            return deleted.length > 0 || added.length > 0;
        }
    },

    computeds: {
        
        location: {
            set(value) {
                if (null == value)
                    return { 'point': null };

                const { lon, lat } = value;
                return { 'point': [lon, lat] };
            },

            get() {
                const location = this.get( 'point' ); 
                if (null == location)
                    return null;

                const [lon, lat] = location;
                
                return lon && lat   
                    ? { lon: +lon, lat: +lat }
                    : null;
            }
        },

        worktime_: {
            set(value) {
                return +value  
                    ? { worktime: value * 3600 }
                    : { worktime: undefined };
            },

            get() {
                const worktime = this.get( 'worktime' );
                return !!worktime
                    ? Math.floor( worktime / 3600 )
                    : undefined;
            },
        },

        assigned_user: {
            set(value) {
                if (!value)
                    return {
                        assigned_user_id: 0,
                        assigned_user_fio: '',
                    };

                const {id, name} = value;

                return {
                    assigned_user_id: id,
                    assigned_user_fio: name,
                };
            },

            get() {
                const id = this.get( 'assigned_user_id' );
                const fio = this.get( 'assigned_user_fio' );

                return !!id
                    ? { id, name: fio, }
                    : null;
            },
        },

        assigned_organization: {
            set(value) {
                if (!value) 
                    return {
                        assigned_organization_id: 0,
                        assigned_user_id: 0,
                        assigned_user_fio: '',
                    };

                return {
                    assigned_organization_id: +value,
                    assigned_user_id: 0,
                    assigned_user_fio: '',
                };
            },

            get() {
                return this.get( 'assigned_organization_id' );
            }
        }
        
    },

} );

export default Task;


// WEBPACK FOOTER //
// ./src/js/model/task.js